import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { radius } from '@libertify/ui';
import Player from 'video.js/dist/types/player';
import { toHHMMSS } from '../utils';
import { CustomTheme, Section, MediaAnalyticsEvents } from '../types';
import { useAntTheme } from '../hooks/useAntTheme';
import { useAnalytics } from '../hooks/useAnalytics';

export const ChaptersOverlayComponent = ({
  player,
  sections,
}: {
  vjsBridgeComponent?: unknown;
  player: Player;
  text?: string;
  sections: Section[];
}) => {
  const [show, setShow] = useState(false);
  const hasBeenOpened = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useAntTheme();
  const { trackEvent } = useAnalytics();

  const toggleShow = useCallback(() => {
    setShow((prev) => !prev);
  }, []);

  // Close on click outside
  useEffect(() => {
    if (!show) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShow(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [show]);

  useEffect(() => {
    if (show) {
      hasBeenOpened.current = true;
      trackEvent(MediaAnalyticsEvents.ChaptersOpen, {
        chapters_count: sections.length,
      });
    } else if (hasBeenOpened.current) {
      trackEvent(MediaAnalyticsEvents.ChaptersClose, {
        chapters_count: sections.length,
      });
    }
  }, [show]);

  if (sections.length === 0 || isMobile) return null;

  return (
    <StyledChaptersWrapper ref={wrapperRef}>
      <StyledControlButton
        className={`vjs-control vjs-button ${show ? 'vjs-button-active' : ''}`}
        type="button"
        aria-label={t('showChapters')}
        aria-expanded={show}
        onClick={toggleShow}
      >
        <svg
          height={20}
          width={20}
          fill="none"
          strokeWidth={1.5}
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 0 1 0 3.75H5.625a1.875 1.875 0 0 1 0-3.75Z"
          />
        </svg>
      </StyledControlButton>

      {show && (
        <StyledChapters>
          {sections.map((section, index) => (
            <ChapterLink
              key={section.startTime}
              title={section.title}
              start={section.startTime}
              onClick={() => {
                trackEvent(MediaAnalyticsEvents.ChapterClick, {
                  chapter_index: index,
                  chapter_start_time: section.startTime,
                });
                player.currentTime(section.startTime);
                player.play();
                setShow(false);
              }}
            />
          ))}
        </StyledChapters>
      )}
    </StyledChaptersWrapper>
  );
};

const ChapterLink = ({
  title,
  start,
  onClick,
}: {
  title: string;
  start: number;
  onClick: React.MouseEventHandler<HTMLDivElement>;
}) => {
  return (
    <StyledChapter onClick={onClick}>
      <StyledChapterTime>{toHHMMSS(start)}</StyledChapterTime>
      <StyledChapterTitle>{title}</StyledChapterTitle>
    </StyledChapter>
  );
};

// --- Chapter segment markers on progress bar (YouTube-style) ---

export const ChapterMarkersComponent = ({
  player,
  sections,
}: {
  vjsBridgeComponent?: unknown;
  player: Player;
  text?: string;
  sections: Section[];
}) => {
  const [duration, setDuration] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    const updateDuration = () => {
      const d = player.duration();
      if (d && d > 0) setDuration(d);
    };

    updateDuration();
    player.on('loadedmetadata', updateDuration);
    player.on('durationchange', updateDuration);
    return () => {
      player.off('loadedmetadata', updateDuration);
      player.off('durationchange', updateDuration);
    };
  }, [player]);

  if (sections.length < 2 || duration === 0) return null;

  // Build segments: each chapter runs from its startTime to the next chapter's startTime (or duration)
  const segments = sections.map((section, i) => {
    const start = section.startTime;
    const end = i < sections.length - 1 ? sections[i + 1].startTime : duration;
    return {
      title: section.title,
      startTime: start,
      startPct: (start / duration) * 100,
      widthPct: ((end - start) / duration) * 100,
    };
  });

  return (
    <StyledSegmentsContainer>
      {segments.map((seg, i) => (
        <StyledSegment
          key={seg.startTime}
          style={{ left: `${seg.startPct}%`, width: `calc(${seg.widthPct}% - 3px)` }}
          onMouseEnter={() => setHoveredIndex(i)}
          onMouseLeave={() => setHoveredIndex(null)}
          onClick={() => {
            player.currentTime(seg.startTime);
            player.play();
          }}
        >
          {hoveredIndex === i && (
            <StyledSegmentTooltip $align={i === segments.length - 1 ? 'right' : i === 0 ? 'left' : 'center'}>
              {seg.title}
            </StyledSegmentTooltip>
          )}
        </StyledSegment>
      ))}
    </StyledSegmentsContainer>
  );
};

const StyledChaptersWrapper = styled.div`
  position: absolute;
  right: 52px;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  height: 50px;
  color: #fff;
`;

const StyledControlButton = styled.button`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 6px;
  border-radius: 4px;
  color: inherit;
  opacity: 0.85;
  transition: opacity 0.15s, background-color 0.15s;

  &:hover,
  &.vjs-button-active {
    opacity: 1;
    background-color: rgba(255, 255, 255, 0.15);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.6);
    outline-offset: 2px;
  }
`;

const StyledChapters = styled.div<{ theme: CustomTheme }>`
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  width: 240px;
  max-height: 320px;
  overflow-y: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  z-index: 3;
  background: rgba(0, 0, 0, 0.88);
  backdrop-filter: blur(12px);
  border-radius: ${radius.box}px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  padding: 4px 0;
  font-family: ${({ theme }) => theme.fontFamily};
`;

const StyledChapter = styled.div`
  cursor: pointer;
  padding: 10px 14px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  transition: background-color 0.12s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const StyledChapterTime = styled.div`
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  opacity: 0.5;
  flex: none;
  min-width: 36px;
  padding-top: 1px;
`;

const StyledChapterTitle = styled.div`
  font-size: 13px;
  line-height: 1.3;
  color: rgba(255, 255, 255, 0.9);
  min-width: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StyledSegmentsContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
`;

const StyledSegment = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  pointer-events: auto;
  cursor: pointer;
  border-right: 3px solid rgba(0, 0, 0, 0.7);

  &:last-child {
    border-right: none;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const StyledSegmentTooltip = styled.div<{ $align: 'left' | 'center' | 'right' }>`
  position: absolute;
  bottom: calc(100% + 8px);
  ${({ $align }) =>
    $align === 'right'
      ? 'right: 0;'
      : $align === 'left'
        ? 'left: 0;'
        : 'left: 50%; transform: translateX(-50%);'}
  background: rgba(0, 0, 0, 0.9);
  color: #fff;
  font-size: 12px;
  line-height: 1.3;
  padding: 6px 10px;
  border-radius: 4px;
  white-space: nowrap;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
`;
